import type React from 'react'
import { useState, useReducer, useContext } from 'react'

import { Button, AlternateButton } from '@/components/DEPRECATED_UI/Button'
import SlideInSidebar from '@/components/DEPRECATED_UI/SlideInSidebar'
import { Select, ItemHeader, Container, ErrorContainer } from '@/components/Style/Common'
import ctx from '@/context/AppContext'
import { graphQLQuery } from '@/hooks/hooks'
import type { FetchSingleApplication } from '@/hooks/useSubject'
import QUERIES from '@/lib/graphql'
import type { Income } from '@/types/AddIncome'

import { buildIncome } from './Behaviour/IncomeBuilder'
import { incomeReducer } from './Behaviour/IncomeReducer'
import { validateIncome } from './Behaviour/IncomeValidator'
import { Property, PropertyDispatch } from './Behaviour/PropertyDispatch'
import BenefitsInput from './BenefitsInput'
import {
  BONUS_FREQUENCY_OPTIONS,
  CONTRACT_OPTIONS,
  CONTRACT_SCHEDULE_OPTIONS,
} from './EmployedIncomeInput'
import { FREQUENCY_OPTIONS } from './IncomePropertiesInput'
import IndependentMeansInput, { MEAN_TYPE_OPTIONS } from './IndependentMeansInput'
import RegularIncomeInput, { STATUS_OPTIONS } from './RegularIncomeInput'
import StudentInput from './StudentInput'
import ThirdPartyPaymentInput from './ThirdPartyPaymentInput'

interface SubjectData {
  customerId: string
  applicationId: string
  subjectId: string
}

interface Props {
  setOpen: (open: boolean) => void
  subjectData: SubjectData
  subjectType: string
  fetchSingleApplication: FetchSingleApplication
}

export const INCOME_OPTIONS = [
  { label: '-', value: '-' },
  { label: 'Regular Income', value: 'regularIncome' },
  { label: 'Independent Means', value: 'independentMeans' },
  { label: 'Benefits', value: 'benefits' },
  { label: 'Third Party 1:1 Payment', value: 'thirdParty' },
  {
    label: 'Student Scholarship or Bursary',
    value: 'student',
    subjectFilter: 'applicant',
  },
]

const AddIncomePanel: React.FC<Props> = ({
  setOpen,
  subjectData,
  subjectType,
  fetchSingleApplication,
}) => {
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(false)
  const [validationComments, setValidationComments] = useState<string[]>([])

  const initialIncomeState: Income = {
    status: STATUS_OPTIONS[0].value,
    contractType: CONTRACT_OPTIONS[0].value,
    startDate: '',
    endDate: '',
    contractSchedule: CONTRACT_SCHEDULE_OPTIONS[0].value,
    incomeProperties: {
      frequency: FREQUENCY_OPTIONS[0].value,
      value: 0,
      averageHoursAWeek: '',
    },
    hasGuaranteedBonus: false,
    bonus: {
      frequency: BONUS_FREQUENCY_OPTIONS[0].value,
      value: 0,
      averageHoursAWeek: '',
    },
    position: '',
    company: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      county: '',
      postcode: '',
      country: '',
    },
    meansType: MEAN_TYPE_OPTIONS[0].value,
    pensionsProvider: '',
    institution: '',
    idNumber: '',
  }

  const { personId } = useContext(ctx)

  const userId = personId ?? ''

  const [income, dispatch] = useReducer(incomeReducer, initialIncomeState)
  const [propertyDispatch] = useState(new PropertyDispatch(dispatch))

  function handleSubmit(e?: React.FormEvent) {
    if (e) {
      e.preventDefault()
    }
    setSending(true)
    setError(false)

    setValidationComments([])
    const [result, comments] = validateIncome(income)

    // eslint-disable-next-line eqeqeq
    if (result == false) {
      setValidationComments(comments)
      setSending(false)
      return
    }

    graphQLQuery({
      query: QUERIES.addIncome,
      variables: {
        addIncomeRequest: {
          subjectData,
          income: buildIncome(income),
          userId,
        },
      },
    })
      .then(resData => resData.json())
      .then(data => {
        console.log(data)
        if (data.errors) {
          setError(true)
          setSending(false)
        } else {
          setOpen(false)
          setSending(false)
          fetchSingleApplication(subjectData.customerId, subjectData.subjectId)
        }
      })
      .catch(err => {
        console.log(err)
        setError(true)
        setSending(false)
      })
  }

  return (
    <SlideInSidebar
      onClose={() => setOpen(false)}
      header={
        <div>
          <h2>Add Income</h2>
        </div>
      }
      content={
        <Container onSubmit={handleSubmit}>
          <div>
            <p>This panel can be used to add a new income to an application</p>
          </div>
          <div>
            <ItemHeader>What type of income would like to add?</ItemHeader>
            <Select
              value={income.type}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                propertyDispatch.set(Property.Type, e.target.value)
              }}
            >
              {INCOME_OPTIONS.filter(
                ({ label, value, subjectFilter }) =>
                  // eslint-disable-next-line eqeqeq
                  subjectFilter == null || subjectFilter == subjectType,
              ).map(({ label, value }, i) => (
                <option key={i} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            {
              // eslint-disable-next-line eqeqeq
              income.type == 'regularIncome' && (
                <RegularIncomeInput income={income} propertyDispatch={propertyDispatch} />
              )
            }
            {
              // eslint-disable-next-line eqeqeq
              income.type == 'independentMeans' && (
                <IndependentMeansInput
                  income={income}
                  propertyDispatch={propertyDispatch}
                />
              )
            }
            {
              // eslint-disable-next-line eqeqeq
              income.type == 'thirdParty' && <ThirdPartyPaymentInput />
            }
            {
              // eslint-disable-next-line eqeqeq
              income.type == 'benefits' && (
                <BenefitsInput income={income} propertyDispatch={propertyDispatch} />
              )
            }
            {
              // eslint-disable-next-line eqeqeq
              income.type == 'student' && (
                <StudentInput income={income} propertyDispatch={propertyDispatch} />
              )
            }
          </div>

          <ErrorContainer>
            {error && (
              <p>
                <strong>Error:</strong> Failed to add income. Please retry in a few
                minutes and contact support if issues persist.
              </p>
            )}
            {validationComments.length > 0 && (
              <p>
                <strong>Error:</strong>
                <ul>
                  {validationComments.map((comment, i) => (
                    <p key={i}>
                      <li>{comment}</li>
                    </p>
                  ))}
                </ul>
              </p>
            )}
          </ErrorContainer>
        </Container>
      }
      footer={
        <div>
          <AlternateButton onClick={() => setOpen(false)}>Cancel</AlternateButton>
          <Button disabled={sending} onClick={() => handleSubmit()}>
            Confirm
          </Button>
        </div>
      }
    />
  )
}

export default AddIncomePanel
